// Platform detail pages: content per area. Same voice and facts as content.ts.
// Sources: research-buyer-psychology (personas, messaging, SEO clusters), research-pitches-brand (product concepts),
// Customer Terms, Trust Centre and Price List 1.0. No invented numbers. No mechanisms, data model or routing logic.
import { forms, parts, type Status } from '../../content';

export type ExplainerKey = 'owner' | 'handover' | 'claim' | 'transfer' | 'tag' | 'forms' | 'parts';

export type Detail = {
  slug: string;
  navTitle: string;
  navLine: string;
  metaTitle: string;
  metaDescription: string;
  kicker: string;
  h1: [string, string, string?];
  lede: string;
  status: Status;
  facts: [string, string][]; // small hero facts (k, v)
  pains: { title: [string, string]; quotes: string[] };
  explainer: ExplainerKey;
  how: { title: [string, string]; steps: { t: string; d: string }[] };
  capabilities: string[]; // keys from content.capabilities
  outcomes: { who: string; t: string; d: string }[];
  faqs: { q: string; a: string }[];
  industries: string[];
  photo: { src: string; alt: string };
};

export const DETAIL_ORDER = ['registration-warranty', 'dealers', 'forms', 'service', 'parts', 'ownership', 'digital-product-passport'] as const;
export type DetailSlug = (typeof DETAIL_ORDER)[number];

export const DETAILS: Record<DetailSlug, Detail> = {
  'registration-warranty': {
    slug: 'registration-warranty',
    navTitle: 'Registration & warranty',
    navLine: 'Warranty that starts at handover, not when the form turns up.',
    metaTitle: 'Product registration and warranty software for manufacturers',
    metaDescription:
      'Replace the paper warranty card. Dealers register each unit at handover, owners register by scanning a QR code, and warranty starts on a date you can prove. Under your brand, built for GDPR.',
    kicker: 'Registration & warranty',
    h1: ['Know who', 'owns it.', ''],
    lede: 'Warranty that starts at handover, on a date you can prove.',
    status: 'live',
    facts: [
      ['Owners', 'No app, no password'],
      ['Dealers', 'Register on the owner\'s behalf'],
      ['Start date', 'Set by your rules'],
    ],
    pains: {
      title: ['The card goes in the bin.', ''],
      quotes: [
        'The warranty card comes back late, or never.',
        'We only find out who owns it when the claim arrives.',
        'Cover runs from the invoice date because nobody can prove when it was handed over.',
      ],
    },
    explainer: 'owner',
    how: {
      title: ['Registered at handover.', ''],
      steps: [
        { t: 'A code on every unit', d: 'A QR or NFC tag on the plate, box or packing slip. Serials come from your ERP.' },
        { t: 'Registered where it happens', d: 'The dealer registers it at handover, or the owner scans. No app, no password.' },
        { t: 'Cover you can prove', d: 'You choose when warranty starts. The date is kept against the serial.' },
        { t: 'A reason to come back', d: 'The same scan opens the manual, parts and help for that unit.' },
      ],
    },
    capabilities: ['identity', 'registration', 'ownership'],
    outcomes: [
      { who: 'Aftersales', t: 'Claims checked against a real start date', d: 'Every claim arrives tied to a serial and a cover date, not a guess from an invoice.' },
      { who: 'Managing director', t: 'Know who owns what you have sold', d: 'An owner for every serial, and the dealer who sold it, in one place you control.' },
      { who: 'Marketing', t: 'An owner list you actually own', d: 'Registered owners with their consent, under your brand, not a reseller\'s.' },
      { who: 'Finance', t: 'Fewer goodwill payouts', d: 'When the start date is known, fewer out-of-warranty claims get paid because nobody could check.' },
    ],
    faqs: [
      { q: 'Do owners need to download an app to register?', a: 'No. They scan the code with their phone camera and get a page for that exact unit. Sign-in uses a one-time code or a passkey, never a password.' },
      { q: 'Can our dealers register a product on the owner\'s behalf?', a: 'Yes. Dealers can register a unit for the customer at handover. You choose whether the owner needs to confirm it, and the owner is always told.' },
      { q: 'When does the warranty start?', a: 'You decide. Cover can start from purchase, delivery, installation, first scan or registration, and the date is recorded against the serial.' },
      { q: 'Is proof of purchase required?', a: 'Only if you want it. Proof of purchase is optional, and you can add approval rules for registrations that need a check.' },
      { q: 'What about products that are already in the field?', a: 'Start with new builds, then add existing serials from your ERP or a spreadsheet. Owners of older units can register once they have a code.' },
      { q: 'Who owns the registration data?', a: 'You do. You are the controller and we are your processor under the DPA built into our terms. Built for GDPR, and you can export it at any time, free.' },
    ],
    photo: { src: '/images/v2/gen/heatpump-commission-b.jpg', alt: 'An installer commissioning a heat pump outside a brick house at sunset' },
    industries: ['heating-heat-pumps', 'agricultural-machinery', 'premium-durables'],
  },

  dealers: {
    slug: 'dealers',
    navTitle: 'Dealers & installers',
    navLine: 'Your channel stays in every sale. Handover and PDI on a phone.',
    metaTitle: 'Dealer and installer portal software for manufacturers',
    metaDescription:
      'Dealer handover and PDI on a phone, the selling dealer credited at registration, and service work routed to the right branch. No app, no extra login per machine. Priced by branch, not by user.',
    kicker: 'Dealers & installers',
    h1: ['Your dealers stay', 'on the record.', ''],
    lede: 'The selling dealer is credited at registration, and service and parts requests go to their branch.',
    status: 'pilot',
    facts: [
      ['Dealer staff', 'No app, works in the browser'],
      ['Handover', 'Signed on a phone, sealed'],
      ['Priced', 'Per branch, unlimited users'],
    ],
    pains: {
      title: ['Dealers think you are going direct.', ''],
      quotes: [
        'If you get the owner\'s details, what stops you selling to them direct?',
        'The PDI form turns up weeks later, if it turns up at all.',
        'Our dealers already juggle ten manufacturer portals.',
      ],
    },
    explainer: 'handover',
    how: {
      title: ['The dealer is part of the record.', ''],
      steps: [
        { t: 'Receive and check', d: 'Dealer staff scan the unit and run your pre-delivery checks on a phone.' },
        { t: 'Hand over together', d: 'Dealer and customer sign on one phone. Warranty starts that day.' },
        { t: 'Credited and kept in', d: 'The selling dealer is recorded. Service and parts requests go to their branch.' },
        { t: 'History at trade-in', d: 'When it comes back, the dealer sees what was done to it, and when.' },
      ],
    },
    capabilities: ['dealers', 'registration', 'service'],
    outcomes: [
      { who: 'Channel manager', t: 'Dealers who want to use it', d: 'Less warranty paperwork for them, credit for the sale, and work sent their way.' },
      { who: 'Dealer principal', t: 'Handover in minutes, not a form and a fax', d: 'PDI and handover done in the yard, with the customer, on a phone.' },
      { who: 'Aftersales', t: 'PDI records you can find at claim time', d: 'Signed and sealed against the serial, not in a filing cabinet at the branch.' },
      { who: 'Managing director', t: 'Owner data without a channel fight', d: 'You see every unit and owner, and the dealer stays attached to every one.' },
    ],
    faqs: [
      { q: 'Will our dealers see this as us going direct?', a: 'It is designed to do the opposite. The selling dealer is credited at registration, service and parts requests go to them, and they see the full history at trade-in.' },
      { q: 'Do dealer staff need an app or a new password?', a: 'No. They work from a scan in the phone browser and sign in with an emailed one-time code or a passkey. Access is limited to their own branch.' },
      { q: 'Can we keep our existing PDI and handover forms?', a: 'Yes. Your forms stay your forms. We digitise them, they are signed on a phone, sealed as a document with an audit page, filed on the unit and still emailed where they go today.' },
      { q: 'How is dealer access priced?', a: 'By branch, not by user. The Partner (Dealer) add-on is £9,000 (€10,800) a year for up to 100 active dealer branches, with unlimited dealer users, plus £2,500 (€3,000) a year for each further 50 branches. It requires the Scale plan.' },
      { q: 'Does it work for installers and service agents too?', a: 'Yes. The same access covers dealers, distributors, installers and service agents, each seeing the units they work on.' },
      { q: 'Can a dealer register a unit for the customer?', a: 'Yes. You choose whether the owner has to confirm, and the owner is always told.' },
    ],
    photo: { src: '/images/v2/gen/dealer-counter.jpg', alt: 'At a machinery dealer\'s parts counter, an advisor compares a worn bearing with a new one while the farmer watches' },
    industries: ['agricultural-machinery', 'heating-heat-pumps', 'lifting-access-equipment'],
  },

  service: {
    slug: 'service',
    navTitle: 'Service, parts & claims',
    navLine: 'Claims that arrive complete. Parts and answers for the exact unit.',
    metaTitle: 'Warranty claims, service and spare parts software for manufacturers',
    metaDescription:
      'Warranty claims that arrive with the serial, build, dealer, photos and history attached. Serial-exact parts, recalls that reach the operator, and AI support grounded in your own manuals.',
    kicker: 'Service, parts & claims',
    h1: ['Claims that arrive', 'complete.', ''],
    lede: 'Every case arrives with the serial, owner, dealer and history.',
    status: 'live',
    facts: [
      ['Claims', 'Serial, photos, history attached'],
      ['Parts', 'Matched to the unit'],
      ['AI', 'Grounded in your documents'],
    ],
    pains: {
      title: ['The serial is missing.', 'The photo is blurred.'],
      quotes: [
        'Which build year is this one? I get asked ten times a day.',
        'The claim came in with no serial and a photo of the wrong side.',
        'The service bulletin reached the dealer. The operator never saw it.',
      ],
    },
    explainer: 'claim',
    how: {
      title: ['Every case starts with a scan.', ''],
      steps: [
        { t: 'The owner reports it from a scan', d: 'Photos, fault and serial arrive together, with warranty checked against the record.' },
        { t: 'Routed to the right people', d: 'To the dealer or your team, with build, history and documents attached.' },
        { t: 'You decide, with the facts', d: 'Approve, decline or ask for the part back. RMAs go to your ERP.' },
        { t: 'The right part, first time', d: 'Parts shown for that serial and build, sold by you or your dealers.' },
      ],
    },
    capabilities: ['service', 'parts', 'support', 'recalls'],
    outcomes: [
      { who: 'Aftersales', t: 'Claims with everything attached', d: 'Serial, build, dealer, photos and service history on every case, before anyone picks up the phone.' },
      { who: 'Service desk', t: 'Fewer "which model is it?" calls', d: 'Owners get the manual and answers for their own unit, and a person when they need one.' },
      { who: 'Finance', t: 'Parts revenue you are giving away', d: 'Genuine parts for the exact unit, sold by you or your dealers instead of a stranger online.' },
      { who: 'Quality', t: 'Bulletins that reach the operator', d: 'Target the affected serials and notify the people who actually use them.' },
    ],
    faqs: [
      { q: 'Can claims be routed to our dealers first?', a: 'Yes. A case can go to the servicing or selling dealer, who adds labour and parts, and you make the decision. The dealer is credited when you approve.' },
      { q: 'Do we keep control of approvals?', a: 'Yes. You approve, decline or require the part back. Approval rules are yours to set.' },
      { q: 'Does the AI answer owners without a person?', a: 'It answers routine questions from your own manuals and that unit\'s records, shows its sources, and hands over to your team when it should. Your team can take over or switch it off.' },
      { q: 'Is our data used to train AI models?', a: 'No shared model is trained on your data, and our AI providers may not train on it. You can switch AI features off, and set a spending cap on each AI meter.' },
      { q: 'Do returns flow back into our ERP?', a: 'Yes. With the Epicor Kinetic connector, returns and RMAs flow back automatically. Other ERPs connect through our API and webhooks.' },
      { q: 'Who is the seller when an owner buys a part?', a: 'You are. Payments go into your own payment account and you are the merchant of record, even when a dealer fulfils or fits the part.' },
    ],
    photo: { src: '/images/v2/gen/fitness-service-b.jpg', alt: 'A technician servicing a commercial treadmill in an empty gym early in the morning' },
    industries: ['commercial-kitchen', 'agricultural-machinery', 'industrial-precision'],
  },

  forms: {
    slug: 'forms',
    navTitle: 'Forms & signatures',
    navLine: 'Your paper forms, signed on a phone and sealed on the unit.',
    metaTitle: 'Digital forms and e-signatures for installers and dealers',
    metaDescription:
      'Turn the commissioning, PDI and inspection PDFs you already use into forms installers and dealers fill in and sign on a phone. Sealed with an audit page, verifiable, and filed on the serial.',
    kicker: forms.eyebrow,
    h1: ['Your forms,', 'signed on site.', ''],
    lede: forms.lede,
    status: forms.status,
    facts: [
      ['Start from', 'The PDF you use today'],
      ['Signed', 'On a phone, in person or by link'],
      ['Kept', 'Sealed and filed on the serial'],
    ],
    pains: {
      title: ['Nobody can find the form.', ''],
      quotes: [
        'The commissioning sheet is a photo on the installer\'s phone.',
        'The PDI arrives weeks after the machine went to work.',
        'At claim time we cannot prove the checks were ever done.',
      ],
    },
    explainer: 'forms',
    how: {
      title: ['Your form, on a phone.', ''],
      steps: forms.steps.map((x) => ({ t: x.t, d: x.d })),
    },
    capabilities: ['dealers', 'registration', 'service'],
    outcomes: [
      { who: 'Aftersales', t: 'Proof before the claim arrives', d: 'The signed commissioning or PDI record is on the serial, sealed, long before anyone asks for it.' },
      { who: 'Installers and dealers', t: 'Signed on site, done', d: 'Tick the checks and sign with the customer on a phone. No app, and nothing to post back to the office.' },
      { who: 'Compliance', t: 'A record you can check', d: 'Every signed form carries an audit page and a verification link.' },
      { who: 'Managing director', t: 'One version of your form', d: 'Approved by your team and used the same way across every dealer and installer.' },
    ],
    faqs: [
      { q: 'Can we use our existing PDF?', a: 'Yes. Upload the form you already use. AI drafts the fields, the required checks and the signer slots from it, and your team reviews them. There is nothing to redesign.' },
      { q: 'Who approves the form before it is used?', a: 'A person on your team. AI only drafts. Nothing is published until someone approves it, and earlier versions are kept.' },
      {
        q: 'Are the signatures legally valid?',
        a: 'They are simple electronic signatures within the meaning of the UK and EU eIDAS Regulations and section 7 of the Electronic Communications Act 2000, not advanced or qualified signatures. Each signed form is sealed as a PDF with an audit page that records the signers\' names, the time, IP address, user agent, the consent text shown and a cryptographic hash of the document, and it can be verified. Whether that is enough depends on the document and the country: some need a deed, a witness or a qualified signature. You choose which documents to sign this way. Clause 10B of our Customer Terms sets this out.',
      },
      { q: 'Where are signed copies kept?', a: 'On the unit. Each sealed form is filed against that serial and any related case, and can still be emailed wherever it goes today. You can export your data at any time.' },
      { q: 'What about gas, electrical or inspection work?', a: 'The form records the work. It does not certify it. The people doing safety-critical work are responsible for being qualified and for what they record. We do not verify registrations or inspect work.' },
      { q: 'Can the owner sign remotely?', a: 'Yes. Signers can sign in person on the same phone, or by link, with the consent wording you set.' },
    ],
    photo: { src: '/images/v2/gen/lifting-inspection.jpg', alt: 'An inspection engineer examining the scissor mechanism of a mobile elevating work platform in a warehouse' },
    industries: ['heating-heat-pumps', 'lifting-access-equipment', 'agricultural-machinery'],
  },

  parts: {
    slug: 'parts',
    navTitle: 'Parts & accessories',
    navLine: 'The right part for that exact unit. Sold by you, not a stranger.',
    metaTitle: 'Spare parts and accessories sales for manufacturers',
    metaDescription:
      'Sell genuine spare parts and accessories matched to each serial, under your brand or through your dealers and installers. Offered when the AI explains a fault, routed to the right partner, written back to your ERP.',
    kicker: parts.eyebrow,
    h1: ['Sell the part', 'that fits.', ''],
    lede: 'Only parts that fit their serial, sold by you or their dealer.',
    status: parts.status,
    facts: [
      ['Matched to', 'Serial, build and options'],
      ['Seller', 'You, under your brand'],
      ['Fitted by', 'You, the dealer or the installer'],
    ],
    pains: {
      title: ['They bought the part online.', 'It did not fit.'],
      quotes: [
        'Which filter fits this one? We answer that by phone every day.',
        'The installer fitted a part we would never have approved.',
        'Once the warranty ends, we never hear from the owner again.',
      ],
    },
    explainer: 'parts',
    how: {
      title: ['Parts matched to the serial.', ''],
      steps: [
        { t: 'Only what fits', d: 'Parts and accessories for that exact build, from your ERP parts data.' },
        { t: 'Offered at the right moment', d: 'When the AI explains a fault, it offers the part. Reminders offer consumables.' },
        { t: 'Your channel, your choice', d: 'Sell direct, or send the order to the dealer or installer to fit.' },
        { t: 'Back into your ERP', d: 'Checkout in your brand. Orders and returns flow back to your ERP.' },
      ],
    },
    capabilities: ['parts', 'support', 'dealers'],
    outcomes: [
      { who: 'Finance', t: 'Parts revenue that stays with you', d: 'Genuine parts for the exact unit, sold by you or your dealers instead of a stranger online.' },
      { who: 'Service desk', t: 'Fewer "which part fits?" calls', d: 'Owners only see what fits their serial, so the question answers itself.' },
      { who: 'Dealers and installers', t: 'Fitting work sent their way', d: 'Orders that need fitting go to the partner who looks after that unit.' },
      { who: 'Managing director', t: 'A relationship after the warranty', d: 'Consumables, upgrades and the next product, sold to an owner you know.' },
    ],
    faqs: [
      { q: 'Can parts go through our dealers?', a: 'Yes. An order can go to the owner\'s dealer or installer to fulfil, fit or collect, and they see it on the unit they look after. You stay the seller and the merchant of record. Dealers do not take payment through their own account in BrandedMark.' },
      { q: 'Do we need our own web shop?', a: 'No. Checkout runs on the unit\'s own page, under your brand. Payments go into your own payment account, and you are the merchant of record. A platform fee of 2% plus £0.25 applies to each payment, as set out in the public price list.' },
      { q: 'How do orders reach our ERP?', a: 'With the Epicor Kinetic connector, orders and returns flow back into your ERP. Other ERPs connect through our API and webhooks. Your ERP stays the master.' },
      { q: 'Can we sell accessories, not just spares?', a: 'Yes. Accessories, consumables, upgrades and service products can be offered against the units they fit, at the moment they are useful, such as a filter reminder before a service.' },
      { q: 'Who handles tax, refunds and returns?', a: 'You do, as the seller, under your own terms of sale. Returns are tracked against the serial and flow back into your ERP.' },
      { q: 'Are the prices on this page real?', a: 'No. Prices in the examples are illustrative. You set every price, discount and delivery charge.' },
    ],
    photo: { src: '/images/v2/gen/kitchen-service-b.jpg', alt: 'A service engineer opening the side panel of a combi oven in a commercial kitchen, a parts case open beside her' },
    industries: ['heating-heat-pumps', 'agricultural-machinery', 'commercial-kitchen'],
  },

  ownership: {
    slug: 'ownership',
    navTitle: 'Ownership transfer',
    navLine: 'The record moves with the product, to the second owner and beyond.',
    metaTitle: 'Product ownership transfer and warranty transfer for manufacturers',
    metaDescription:
      'When a product is sold on, the next owner takes over the registration with consent and keeps the service history and warranty. Know your second and third owners, not just the first.',
    kicker: 'Ownership transfer',
    h1: ['The history goes', 'with it.', ''],
    lede: 'The next owner takes over with consent. The last one stays private.',
    status: 'live',
    facts: [
      ['Consent', 'The owner is always told'],
      ['Privacy', 'Previous owner stays private'],
      ['Warranty', 'Follows your rules'],
    ],
    pains: {
      title: ['The second owner is invisible.', ''],
      quotes: [
        'The service book went with the old owner.',
        'Dealers take trade-ins blind.',
        'Warranty transfer happens by email, if anyone asks.',
      ],
    },
    explainer: 'transfer',
    how: {
      title: ['The next owner takes over.', ''],
      steps: [
        { t: 'The owner hands it on', d: 'From their unit page, or through a dealer at trade-in. You can require approval.' },
        { t: 'The next owner takes over', d: 'They scan, confirm and register. The previous owner is told.' },
        { t: 'History kept, details private', d: 'Service records and cover stay with the unit. The previous owner\'s details stay private.' },
        { t: 'Warranty on your terms', d: 'Continue, restart, shorten, end or wait for approval, set per product.' },
      ],
    },
    capabilities: ['ownership', 'registration', 'dealers'],
    outcomes: [
      { who: 'Managing director', t: 'Know your second and third owners', d: 'The people who buy your products used are often your most loyal. Now you can reach them.' },
      { who: 'Dealers', t: 'Trade-ins with the history attached', d: 'What was done to the machine, and when, before they price it.' },
      { who: 'Aftersales', t: 'Warranty transfers without the email chain', d: 'Your rules applied the same way every time, recorded against the serial.' },
      { who: 'Owners', t: 'A machine that is easier to sell', d: 'A documented history makes a used product worth more to the next buyer.' },
    ],
    faqs: [
      { q: 'Does the new owner see the previous owner\'s details?', a: 'No. The new owner gets the unit\'s history and documents, not the previous owner\'s personal details.' },
      { q: 'What happens to the warranty when a product is sold?', a: 'You decide per product. Cover can continue, restart, shorten, end, or wait for your approval.' },
      { q: 'Does the current owner have to agree?', a: 'You choose the policy, and the recorded owner is always told. We recommend requiring the owner to confirm a transfer before it completes.' },
      { q: 'Can a dealer handle the transfer at trade-in?', a: 'Yes. A dealer can take a unit into trade-in and pass it to the next owner, under the policy you set.' },
      { q: 'Does the next owner need an app or an account?', a: 'No. They scan the code on the product and register with a one-time code or a passkey.' },
    ],
    photo: { src: '/images/v2/gen/hifi-owner.jpg', alt: 'A hand turning the volume knob of a well kept stereo amplifier on a walnut sideboard' },
    industries: ['premium-durables', 'lifting-access-equipment', 'commercial-kitchen'],
  },

  'digital-product-passport': {
    slug: 'digital-product-passport',
    navTitle: 'Digital Product Passport',
    navLine: 'Same code, two jobs. What is decided, and what is not.',
    metaTitle: 'Digital Product Passport (DPP) software for manufacturers: ESPR, dated and plain',
    metaDescription:
      'Digital Product Passports on the same GS1 Digital Link code that registers owners and starts warranties. What ESPR has decided, what it has not, and the battery passport date. Last verified 20 October 2026.',
    kicker: 'Digital Product Passport',
    h1: ['Passports that', 'do more.', ''],
    lede: 'The code that carries your passport also registers owners and sells parts.',
    status: 'live',
    facts: [
      ['Identifier', 'GS1 Digital Link, your domain'],
      ['Data', 'Lot and certificate data from your ERP'],
      ['Last verified', '20 Oct 2026'],
    ],
    pains: {
      title: ['Most of the dates you hear are wrong.', ''],
      quotes: [
        'A vendor told us we had to comply by July. They were wrong.',
        'Is our category even in scope?',
        'What happens to the passports if the vendor disappears?',
      ],
    },
    explainer: 'tag',
    how: {
      title: ['How the passport works.', ''],
      steps: [
        { t: 'An identifier you own', d: 'GS1 Digital Link codes on your own domain.' },
        { t: 'Data attached at source', d: 'Lot, material and certificate data from your ERP, attached as it ships.' },
        { t: 'The right view for each visitor', d: 'Owners, repairers and authorities each get their own view of one scan.' },
        { t: 'Fields added as acts land', d: 'When your group\'s act is adopted, we add its fields. If it names a QR code, your labels stay.' },
      ],
    },
    capabilities: ['dpp', 'identity'],
    outcomes: [
      { who: 'Compliance', t: 'One identifier and carrier, ready now', d: 'Ready for passport data as each delegated act lands, usually with no second code on the product.' },
      { who: 'Managing director', t: 'A compliance cost that pays its way', d: 'The same code registers owners, starts warranties and sells parts long before a passport is due.' },
      { who: 'IT', t: 'Data from the system that already holds it', d: 'Lots and certificates read from your ERP, not keyed into another portal.' },
      { who: 'Customers', t: 'Certificates that travel with the goods', d: 'Material certificates and lot data on the delivery itself, one scan away.' },
    ],
    faqs: [
      { q: 'Are our products in scope for the Digital Product Passport?', a: 'It depends on your product group and its delegated act. No ESPR product group has a passport date yet. Machinery, heat pumps and commercial kitchen equipment are not on the Commission\'s 2025 to 2030 working plan at all, though batteries inside them may need a battery passport. On the call we will tell you plainly what is decided for your products and what is not.' },
      { q: 'Do we need a separate QR code for the passport?', a: 'Usually not. Each delegated act names the data carrier to use. Where that is a QR code, the same GS1 Digital Link code owners scan to register can carry the passport, with each visitor shown what their role allows. GS1 Digital Link fits the identifier rules well, though the law leaves the choice open.' },
      { q: 'When does the battery passport apply?', a: 'Under the Battery Regulation (EU) 2023/1542, the battery passport applies from 18 February 2027 for electric vehicle, light means of transport and industrial batteries over 2 kWh. Home storage batteries count as industrial batteries.' },
      { q: 'Are you a certified DPP service provider?', a: 'No certification scheme for DPP service providers exists yet. We design to the published requirements of Regulation (EU) 2024/1781 and will seek certification when a scheme exists. The rules for service providers are expected in 2027.' },
      { q: 'What happens to our passports if we leave BrandedMark?', a: 'They keep working. The regulation itself expects a passport to outlive the company behind it, with a back-up copy held by a passport service provider, and we are designed for that role. We redirect your GS1 links and passports to a new host free for 12 months, or keep them live, read-only, with Link Continuity. If the codes use your own domain, you can repoint them yourself.' },
      { q: 'Do passports stay up if our account is suspended?', a: 'Yes. Public passports and product links stay up during a suspension wherever reasonably possible, so products in the market are not affected.' },
    ],
    photo: { src: '/images/v2/gen/factory-endofline.jpg', alt: 'A line worker applying a label to a heat pump at the end of the production line' },
    industries: ['industrial-precision', 'heating-heat-pumps', 'premium-durables'],
  },
};

// DPP: what is decided vs not. Sources: Regulation (EU) 2024/1781 (ESPR), Regulation (EU) 2023/1542 (batteries),
// Regulation (EU) 2023/1230 (machinery), Implementing Regulation (EU) 2026/1778, Implementing Decision (EU) 2026/1736.
// Checked against the Official Journal texts on 20 October 2026 (scratchpad dpp-facts.md).
export const DPP_LAST_VERIFIED = '20 October 2026';

export const DPP_DECIDED: { t: string; d: string; src: string }[] = [
  {
    t: 'The framework is law',
    d: 'ESPR is in force. Rules usually apply 18 months or more after each group\'s act.',
    src: 'Regulation (EU) 2024/1781, Art 4',
  },
  {
    t: 'A central registry, now live',
    d: 'Live since 20 July 2026. It holds identifiers only.',
    src: 'Regulation (EU) 2024/1781, Art 13; Implementing Regulation (EU) 2026/1778',
  },
  {
    t: 'Passports must outlive the company',
    d: 'A passport service provider holds a back-up copy.',
    src: 'Regulation (EU) 2024/1781, Art 10(4), Art 11(e)',
  },
  {
    t: 'Batteries have their own date',
    d: 'From 18 February 2027 for EV, light means of transport and industrial batteries over 2 kWh.',
    src: 'Regulation (EU) 2023/1542, Art 77; recital 15',
  },
  {
    t: 'Machinery instructions can go digital',
    d: 'From 20 January 2027, online for at least 10 years, with paper free if asked at purchase.',
    src: 'Regulation (EU) 2023/1230, Art 10(7)',
  },
];

// Why start before an act lands. Each point rests on a dated fact above, not on a guessed deadline.
export const DPP_AHEAD: { t: string; d: string }[] = [
  {
    t: 'Part of it may already be due',
    d: 'Machinery instructions can go digital from 20 January 2027 and battery passports apply from 18 February 2027. If your product is machinery or carries a large battery, that is months away.',
  },
  {
    t: 'What you ship now is still in use later',
    d: 'Durable products last for years. A code on every unit from today means those units can carry a passport when your act lands, without relabelling machines already in the field.',
  },
  {
    t: 'The data is the slow part',
    d: 'Material, lot and certificate data sits in your ERP and with your suppliers. Getting it attached to each serial takes longer than printing a code, so start while there is no deadline.',
  },
  {
    t: 'It pays for itself first',
    d: 'The same code registers owners, starts warranties and sells parts from the first unit. The passport is added to something already earning.',
  },
];

export const DPP_UNDECIDED: { t: string; d: string }[] = [
  { t: 'A date for any ESPR product group', d: 'No product group act is adopted yet. Ask anyone quoting a date to show you the act.' },
  { t: 'The exact data for your group', d: 'Each act sets the fields. The working plan\'s years are indicative.' },
  { t: 'Certification for service providers', d: 'No scheme exists yet. Rules are expected in 2027.' },
  { t: 'Whether you are in scope at all', d: 'The 2025 to 2030 plan leaves out heat pumps, machinery and professional cooking equipment. Batteries inside may still need one.' },
];
